import { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";
import { DEFAULT_SITE, mediaUrl } from "../../lib/siteContent";
import WeeklyAvailabilityPanel from "../../components/WeeklyAvailabilityPanel";
import DmyDateInput from "../../components/DmyDateInput";

function deepMerge(base, extra) {
  if (!extra || typeof extra !== "object") return JSON.parse(JSON.stringify(base));
  if (Array.isArray(base)) return Array.isArray(extra) ? extra : JSON.parse(JSON.stringify(base));
  const out = JSON.parse(JSON.stringify(base));
  Object.keys({ ...base, ...extra }).forEach((k) => {
    if (Array.isArray(base[k]) || Array.isArray(extra[k])) {
      if (Array.isArray(extra[k])) out[k] = extra[k];
    } else if (extra[k] && typeof extra[k] === "object" && base[k] && typeof base[k] === "object") {
      out[k] = deepMerge(base[k], extra[k]);
    } else if (extra[k] !== undefined && extra[k] !== null && extra[k] !== "") {
      out[k] = extra[k];
    }
  });
  return out;
}

function localIsoDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDaysIso(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return localIsoDate(dt);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm text-[#311b52]">
      <span className="font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm";

function HtmlEditor({ value, onChange, label }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-1 mb-1">
        {[["bold", "B"], ["italic", "I"], ["underline", "U"]].map(([cmd, mark]) => (
          <button key={cmd} type="button" className="px-2 py-0.5 text-xs border rounded"
            onMouseDown={(e) => { e.preventDefault(); document.execCommand(cmd, false); }}>
            {mark}
          </button>
        ))}
        <button type="button" className="px-2 py-0.5 text-xs border rounded"
          onMouseDown={(e) => {
            e.preventDefault();
            const url = window.prompt("Link URL or #terms / #privacy / #medical");
            if (url) document.execCommand("createLink", false, url);
          }}>
          Link
        </button>
      </div>
      <div
        contentEditable
        suppressContentEditableWarning
        className={`${inputCls} min-h-[140px] prose prose-sm max-w-none`}
        dangerouslySetInnerHTML={undefined}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        ref={(el) => {
          if (!el) return;
          const next = value || "";
          if (el.dataset.sync === next) return;
          if (document.activeElement === el) return;
          el.innerHTML = next;
          el.dataset.sync = next;
        }}
      />
      <textarea className={`${inputCls} mt-2 font-mono text-xs min-h-[80px]`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function WebsiteCms({ onMsg }) {
  const { getAuthHeaders } = useAuth();
  const [site, setSite] = useState(DEFAULT_SITE);
  const [saving, setSaving] = useState(false);
  const [cmsReady, setCmsReady] = useState(0);

  useEffect(() => {
    axios.get(`${config.API}/cms/site`)
      .then(({ data }) => setSite(deepMerge(DEFAULT_SITE, data.sections || data)))
      .catch(() => {})
      .finally(() => setCmsReady((n) => n + 1));
  }, []);

  const patch = (section, extra) => setSite((prev) => ({ ...prev, [section]: { ...prev[section], ...extra } }));

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${config.API}/admin/cms/upload`, fd, { headers: getAuthHeaders() });
    return data.url;
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${config.API}/admin/cms/site`, {
        key: "site",
        title: "Website",
        published: true,
        sections: site,
      }, { headers: getAuthHeaders() });
      onMsg?.("Website content saved — public pages will pick this up immediately.");
    } catch (e) {
      onMsg?.(e.response?.data?.detail || "Could not save website content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="website-cms">
      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Hero</h2>
        <Field label="Eyebrow"><input className={inputCls} value={site.hero.eyebrow} onChange={(e) => patch("hero", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.hero.headline} onChange={(e) => patch("hero", { headline: e.target.value })} /></Field>
        <Field label="Accent word"><input className={inputCls} value={site.hero.headline_accent} onChange={(e) => patch("hero", { headline_accent: e.target.value })} /></Field>
        <Field label="Subhead"><textarea className={inputCls} rows={3} value={site.hero.subhead} onChange={(e) => patch("hero", { subhead: e.target.value })} /></Field>
        <Field label="Hero video URL (optional)">
          <input className={inputCls} value={site.hero.video_url || ""} onChange={(e) => patch("hero", { video_url: e.target.value })} placeholder="https://… or upload below" />
        </Field>
        <p className="text-xs text-[#6b7280]">Carousel photos (up to 5). These rotate on the homepage hero.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {(site.hero.images || []).slice(0, 5).map((img, idx) => (
            <div key={idx} className="border border-[#673c96]/10 rounded-lg p-2">
              <p className="text-xs text-[#6b7280] mb-1">Photo {idx + 1}</p>
              {img ? <img src={img.startsWith("/api/") ? `${config.API.replace(/\/api$/, "")}${img}` : img} alt="" className="h-24 w-full object-cover rounded-lg mb-2" /> : null}
              <input type="file" accept="image/*" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const url = await upload(file);
                  const images = [...(site.hero.images || [])];
                  images[idx] = url;
                  patch("hero", { images: images.filter(Boolean).slice(0, 5) });
                  onMsg?.("Photo uploaded");
                } catch (err) {
                  onMsg?.(err.response?.data?.detail || "Upload failed");
                }
              }} />
              <button type="button" className="mt-1 text-xs text-[#ee4036]"
                onClick={() => patch("hero", { images: (site.hero.images || []).filter((_, i) => i !== idx) })}>
                Remove
              </button>
            </div>
          ))}
        </div>
        {(site.hero.images || []).length < 5 && (
          <button type="button" className="text-sm font-semibold text-[#673c96]"
            onClick={() => patch("hero", { images: [...(site.hero.images || []), ""] })}>
            + Add carousel photo
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Site logo</h2>
        <p className="text-xs text-[#6b7280]">Shown in the header, footer, and staff portals. Leave empty to use the default BookMyJab mark.</p>
        {(site.navbar?.logo || site.footer?.logo) ? (
          <img src={mediaUrl(site.navbar?.logo || site.footer?.logo)} alt="Current logo" className="h-16 object-contain" />
        ) : null}
        <input type="file" accept="image/*" onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          try {
            const url = await upload(file);
            patch("navbar", { logo: url });
            patch("footer", { logo: url });
            onMsg?.("Logo uploaded — save website content to publish");
          } catch (err) {
            onMsg?.(err.response?.data?.detail || "Upload failed");
          }
        }} />
        {(site.navbar?.logo || site.footer?.logo) ? (
          <button type="button" className="text-xs text-[#ee4036]" onClick={() => { patch("navbar", { logo: "" }); patch("footer", { logo: "" }); }}>
            Revert to default logo
          </button>
        ) : null}
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Moving banner (under header)</h2>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={site.ticker?.enabled !== false} onChange={(e) => patch("ticker", { enabled: e.target.checked })} />
          Show scrolling banner on the homepage
        </label>
        <Field label="Banner text">
          <input className={inputCls} value={site.ticker?.text || ""} onChange={(e) => patch("ticker", { text: e.target.value })} placeholder="At-home vaccination across Gurgaon · Certified nurses…" />
        </Field>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">My Partners carousel</h2>
        <p className="text-xs text-[#6b7280]">Homepage logo carousel. Add brand marks; they rotate four at a time.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={site.my_partners?.enabled !== false} onChange={(e) => patch("my_partners", { enabled: e.target.checked })} />
          Show My Partners on the homepage
        </label>
        <Field label="Eyebrow"><input className={inputCls} value={site.my_partners?.eyebrow || ""} onChange={(e) => patch("my_partners", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.my_partners?.headline || ""} onChange={(e) => patch("my_partners", { headline: e.target.value })} /></Field>
        {(site.my_partners?.items || []).map((item, idx) => (
          <div key={idx} className="border border-[#673c96]/10 rounded-lg p-3 space-y-2">
            <div className="flex justify-between">
              <p className="text-xs font-semibold text-[#673c96]">Partner {idx + 1}</p>
              <button type="button" className="text-xs text-[#ee4036]"
                onClick={() => patch("my_partners", { items: (site.my_partners.items || []).filter((_, i) => i !== idx) })}>
                Remove
              </button>
            </div>
            <input className={inputCls} value={item.name || ""} placeholder="Partner name"
              onChange={(e) => {
                const items = [...(site.my_partners.items || [])];
                items[idx] = { ...items[idx], name: e.target.value };
                patch("my_partners", { items });
              }} />
            <input className={inputCls} value={item.url || ""} placeholder="https:// (optional)"
              onChange={(e) => {
                const items = [...(site.my_partners.items || [])];
                items[idx] = { ...items[idx], url: e.target.value };
                patch("my_partners", { items });
              }} />
            {item.logo ? <img src={mediaUrl(item.logo)} alt="" className="h-12 object-contain" /> : null}
            <input type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await upload(file);
                const items = [...(site.my_partners.items || [])];
                items[idx] = { ...items[idx], logo: url };
                patch("my_partners", { items });
                onMsg?.("Partner logo uploaded");
              } catch (err) {
                onMsg?.(err.response?.data?.detail || "Upload failed");
              }
            }} />
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("my_partners", { items: [...(site.my_partners?.items || []), { name: "", logo: "", url: "" }] })}>
          + Add partner
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Serving Gurgaon</h2>
        <p className="text-xs text-[#6b7280]">Homepage coverage section. Neighbourhoods are comma-separated chips.</p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={site.serving?.enabled !== false} onChange={(e) => patch("serving", { enabled: e.target.checked })} />
          Show Serving Gurgaon on the homepage
        </label>
        <Field label="Eyebrow"><input className={inputCls} value={site.serving?.eyebrow || ""} onChange={(e) => patch("serving", { eyebrow: e.target.value })} /></Field>
        <Field label="Title"><input className={inputCls} value={site.serving?.title || ""} onChange={(e) => {
          const title = e.target.value;
          patch("serving", { title });
          patch("contact", { serving_title: title });
        }} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={3} value={site.serving?.body || ""} onChange={(e) => {
          const body = e.target.value;
          patch("serving", { body });
          patch("contact", { serving_body: body });
        }} /></Field>
        <Field label="Areas (comma separated)"><input className={inputCls} value={site.serving?.areas || ""} onChange={(e) => patch("serving", { areas: e.target.value })} placeholder="DLF Phases, Golf Course Road, Sector 54-57" /></Field>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Banner (below partner photos)</h2>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!site.banner.enabled} onChange={(e) => patch("banner", { enabled: e.target.checked })} />
          Show banner on homepage
        </label>
        <Field label="Title"><input className={inputCls} value={site.banner.title} onChange={(e) => patch("banner", { title: e.target.value })} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={2} value={site.banner.body} onChange={(e) => patch("banner", { body: e.target.value })} /></Field>
        <Field label="Button label"><input className={inputCls} value={site.banner.cta_label} onChange={(e) => patch("banner", { cta_label: e.target.value })} /></Field>
        <Field label="Button link"><input className={inputCls} value={site.banner.cta_url} onChange={(e) => patch("banner", { cta_url: e.target.value })} /></Field>
        <Field label="Image or video">
          <input type="file" accept="image/*,video/*" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const url = await upload(file);
              if (file.type.startsWith("video")) patch("banner", { video_url: url, image: "" });
              else patch("banner", { image: url, video_url: "" });
              onMsg?.("Banner media uploaded");
            } catch (err) {
              onMsg?.(err.response?.data?.detail || "Upload failed");
            }
          }} />
        </Field>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Testimonials</h2>
        <Field label="Heading"><input className={inputCls} value={site.testimonials.heading} onChange={(e) => patch("testimonials", { heading: e.target.value })} /></Field>
        {(site.testimonials.items || []).map((item, idx) => (
          <div key={idx} className="grid sm:grid-cols-12 gap-2">
            <input className={`${inputCls} sm:col-span-7`} value={item.quote} placeholder="Quote"
              onChange={(e) => {
                const items = [...site.testimonials.items];
                items[idx] = { ...items[idx], quote: e.target.value };
                patch("testimonials", { items });
              }} />
            <input className={`${inputCls} sm:col-span-4`} value={item.author} placeholder="Author"
              onChange={(e) => {
                const items = [...site.testimonials.items];
                items[idx] = { ...items[idx], author: e.target.value };
                patch("testimonials", { items });
              }} />
            <button type="button" className="sm:col-span-1 text-[#ee4036] text-sm"
              onClick={() => patch("testimonials", { items: site.testimonials.items.filter((_, i) => i !== idx) })}>×</button>
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("testimonials", { items: [...(site.testimonials.items || []), { quote: "", author: "" }] })}>
          + Add testimonial
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Get in Touch</h2>
        <p className="text-xs text-[#6b7280]">Contact details and serving locations on the homepage.</p>
        <Field label="Eyebrow"><input className={inputCls} value={site.contact?.eyebrow || ""} onChange={(e) => patch("contact", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.contact?.headline || ""} onChange={(e) => patch("contact", { headline: e.target.value })} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={3} value={site.contact?.body || ""} onChange={(e) => patch("contact", { body: e.target.value })} /></Field>
        <Field label="WhatsApp label"><input className={inputCls} value={site.contact?.whatsapp_label || ""} onChange={(e) => patch("contact", { whatsapp_label: e.target.value })} /></Field>
        <Field label="WhatsApp hint"><input className={inputCls} value={site.contact?.whatsapp_hint || ""} onChange={(e) => patch("contact", { whatsapp_hint: e.target.value })} /></Field>
        <Field label="Email"><input className={inputCls} value={site.contact?.email || ""} onChange={(e) => patch("contact", { email: e.target.value })} /></Field>
        <Field label="Phone (shown on site)"><input className={inputCls} value={site.contact?.phone || ""} onChange={(e) => patch("contact", { phone: e.target.value })} placeholder="+91 9873 55 4343" /></Field>
        <Field label="Serving locations title"><input className={inputCls} value={site.contact?.serving_title || ""} onChange={(e) => patch("contact", { serving_title: e.target.value })} /></Field>
        <Field label="Serving locations copy"><textarea className={inputCls} rows={3} value={site.contact?.serving_body || ""} onChange={(e) => patch("contact", { serving_body: e.target.value })} /></Field>
        <Field label="Hours label"><input className={inputCls} value={site.contact?.hours_label || ""} onChange={(e) => patch("contact", { hours_label: e.target.value })} /></Field>
        <Field label="Weekday hours"><input className={inputCls} value={site.contact?.hours_weekday || ""} onChange={(e) => patch("contact", { hours_weekday: e.target.value })} /></Field>
        <Field label="Sunday hours"><input className={inputCls} value={site.contact?.hours_sunday || ""} onChange={(e) => patch("contact", { hours_sunday: e.target.value })} /></Field>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Medical Partners</h2>
        <p className="text-xs text-[#6b7280]">Add or remove doctors shown on the homepage.</p>
        <Field label="Eyebrow"><input className={inputCls} value={site.partners?.eyebrow || ""} onChange={(e) => patch("partners", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.partners?.headline || ""} onChange={(e) => patch("partners", { headline: e.target.value })} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={2} value={site.partners?.body || ""} onChange={(e) => patch("partners", { body: e.target.value })} /></Field>
        <Field label="Button label"><input className={inputCls} value={site.partners?.cta_label || ""} onChange={(e) => patch("partners", { cta_label: e.target.value })} /></Field>
        <Field label="Note under doctors"><input className={inputCls} value={site.partners?.cta_note || ""} onChange={(e) => patch("partners", { cta_note: e.target.value })} /></Field>
        {(site.partners?.doctors || []).map((doc, idx) => (
          <div key={doc.id || idx} className="border border-[#673c96]/10 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-[#673c96]">Doctor {idx + 1}</p>
              <button type="button" className="text-xs text-[#ee4036]"
                onClick={() => patch("partners", { doctors: (site.partners.doctors || []).filter((_, i) => i !== idx) })}>
                Remove
              </button>
            </div>
            <input className={inputCls} value={doc.name || ""} placeholder="Name"
              onChange={(e) => {
                const doctors = [...(site.partners.doctors || [])];
                doctors[idx] = { ...doctors[idx], name: e.target.value };
                patch("partners", { doctors });
              }} />
            <input className={inputCls} value={doc.role || ""} placeholder="Role badge"
              onChange={(e) => {
                const doctors = [...(site.partners.doctors || [])];
                doctors[idx] = { ...doctors[idx], role: e.target.value };
                patch("partners", { doctors });
              }} />
            <input className={inputCls} value={doc.credentials || ""} placeholder="Credentials"
              onChange={(e) => {
                const doctors = [...(site.partners.doctors || [])];
                doctors[idx] = { ...doctors[idx], credentials: e.target.value };
                patch("partners", { doctors });
              }} />
            <textarea className={inputCls} rows={3} value={doc.intro || ""} placeholder="Introduction"
              onChange={(e) => {
                const doctors = [...(site.partners.doctors || [])];
                doctors[idx] = { ...doctors[idx], intro: e.target.value };
                patch("partners", { doctors });
              }} />
            {doc.image ? <img src={mediaUrl(doc.image)} alt="" className="h-20 w-20 object-cover rounded-lg" /> : null}
            <input type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await upload(file);
                const doctors = [...(site.partners.doctors || [])];
                doctors[idx] = { ...doctors[idx], image: url };
                patch("partners", { doctors });
                onMsg?.("Doctor photo uploaded");
              } catch (err) {
                onMsg?.(err.response?.data?.detail || "Upload failed");
              }
            }} />
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("partners", { doctors: [...(site.partners?.doctors || []), { id: `doc-${Date.now()}`, name: "", role: "", credentials: "", intro: "", image: "" }] })}>
          + Add doctor
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Why Choose Us</h2>
        <Field label="Eyebrow"><input className={inputCls} value={site.why?.eyebrow || ""} onChange={(e) => patch("why", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.why?.headline || ""} onChange={(e) => patch("why", { headline: e.target.value })} /></Field>
        {(site.why?.reasons || []).map((r, idx) => (
          <div key={idx} className="grid sm:grid-cols-12 gap-2 items-start">
            <select className={`${inputCls} sm:col-span-3`} value={r.icon || "ShieldCheck"}
              onChange={(e) => {
                const reasons = [...(site.why.reasons || [])];
                reasons[idx] = { ...reasons[idx], icon: e.target.value };
                patch("why", { reasons });
              }}>
              {["ShieldCheck", "Sparkles", "Home", "Users", "MessageCircle", "FileText", "Stethoscope", "Heart", "Baby", "Plane"].map((icon) => (
                <option key={icon} value={icon}>{icon}</option>
              ))}
            </select>
            <input className={`${inputCls} sm:col-span-3`} value={r.title || ""} placeholder="Title"
              onChange={(e) => {
                const reasons = [...(site.why.reasons || [])];
                reasons[idx] = { ...reasons[idx], title: e.target.value };
                patch("why", { reasons });
              }} />
            <textarea className={`${inputCls} sm:col-span-5`} rows={2} value={r.desc || ""} placeholder="Description"
              onChange={(e) => {
                const reasons = [...(site.why.reasons || [])];
                reasons[idx] = { ...reasons[idx], desc: e.target.value };
                patch("why", { reasons });
              }} />
            <button type="button" className="sm:col-span-1 text-[#ee4036] text-sm"
              onClick={() => patch("why", { reasons: (site.why.reasons || []).filter((_, i) => i !== idx) })}>×</button>
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("why", { reasons: [...(site.why?.reasons || []), { icon: "ShieldCheck", title: "", desc: "" }] })}>
          + Add reason
        </button>
        <p className="text-xs text-[#6b7280] pt-2">Carousel photos</p>
        {(site.why?.images || []).map((img, idx) => (
          <div key={idx} className="border border-[#673c96]/10 rounded-lg p-2 space-y-2">
            {(img.url || img) ? <img src={mediaUrl(img.url || img)} alt="" className="h-24 w-full object-cover rounded-lg" /> : null}
            <input className={inputCls} value={img.caption || ""} placeholder="Caption"
              onChange={(e) => {
                const images = [...(site.why.images || [])];
                images[idx] = { ...(typeof images[idx] === "object" ? images[idx] : { url: images[idx] }), caption: e.target.value };
                patch("why", { images });
              }} />
            <input type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await upload(file);
                const images = [...(site.why.images || [])];
                images[idx] = { ...(typeof images[idx] === "object" ? images[idx] : {}), url, caption: images[idx]?.caption || "" };
                patch("why", { images });
                onMsg?.("Photo uploaded");
              } catch (err) {
                onMsg?.(err.response?.data?.detail || "Upload failed");
              }
            }} />
            <button type="button" className="text-xs text-[#ee4036]"
              onClick={() => patch("why", { images: (site.why.images || []).filter((_, i) => i !== idx) })}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("why", { images: [...(site.why?.images || []), { url: "", caption: "" }] })}>
          + Add carousel photo
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Homepage catalogue</h2>
        <p className="text-xs text-[#6b7280]">The five category cards on the homepage. This is not the live vaccine list.</p>
        <Field label="Eyebrow"><input className={inputCls} value={site.catalogue?.eyebrow || ""} onChange={(e) => patch("catalogue", { eyebrow: e.target.value })} /></Field>
        <Field label="Headline"><input className={inputCls} value={site.catalogue?.headline || ""} onChange={(e) => patch("catalogue", { headline: e.target.value })} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={3} value={site.catalogue?.body || ""} onChange={(e) => patch("catalogue", { body: e.target.value })} /></Field>
        <Field label="Button label"><input className={inputCls} value={site.catalogue?.cta_label || ""} onChange={(e) => patch("catalogue", { cta_label: e.target.value })} /></Field>
        {(site.catalogue?.categories || []).map((cat, idx) => (
          <div key={cat.id || idx} className="grid sm:grid-cols-12 gap-2 items-start">
            <select className={`${inputCls} sm:col-span-3`} value={cat.id || "other"}
              onChange={(e) => {
                const categories = [...(site.catalogue.categories || [])];
                categories[idx] = { ...categories[idx], id: e.target.value };
                patch("catalogue", { categories });
              }}>
              {["paediatric", "adults", "elderly", "travel", "other"].map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
            <input className={`${inputCls} sm:col-span-3`} value={cat.title || ""} placeholder="Title"
              onChange={(e) => {
                const categories = [...(site.catalogue.categories || [])];
                categories[idx] = { ...categories[idx], title: e.target.value };
                patch("catalogue", { categories });
              }} />
            <input className={`${inputCls} sm:col-span-5`} value={cat.examples || ""} placeholder="Examples, e.g. Influenza · HPV"
              onChange={(e) => {
                const categories = [...(site.catalogue.categories || [])];
                categories[idx] = { ...categories[idx], examples: e.target.value };
                patch("catalogue", { categories });
              }} />
            <button type="button" className="sm:col-span-1 text-[#ee4036] text-sm"
              onClick={() => patch("catalogue", { categories: (site.catalogue.categories || []).filter((_, i) => i !== idx) })}>×</button>
          </div>
        ))}
        <button type="button" className="text-sm font-semibold text-[#673c96]"
          onClick={() => patch("catalogue", { categories: [...(site.catalogue?.categories || []), { id: "other", title: "", examples: "" }] })}>
          + Add category
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Header & footer</h2>
        <Field label="Navbar tagline"><input className={inputCls} value={site.navbar?.tagline || ""} onChange={(e) => patch("navbar", { tagline: e.target.value })} /></Field>
        <Field label="Footer tagline"><input className={inputCls} value={site.footer?.tagline || ""} onChange={(e) => patch("footer", { tagline: e.target.value })} /></Field>
        <Field label="Footer blurb"><textarea className={inputCls} rows={3} value={site.footer.blurb} onChange={(e) => patch("footer", { blurb: e.target.value })} /></Field>
        <Field label="Founder quote"><textarea className={inputCls} rows={3} value={site.footer.founder_quote} onChange={(e) => patch("footer", { founder_quote: e.target.value })} /></Field>
        <Field label="Founder credit"><input className={inputCls} value={site.footer.founder_credit} onChange={(e) => patch("footer", { founder_credit: e.target.value })} /></Field>
        <Field label="Support phone"><input className={inputCls} value={site.footer.phone || ""} onChange={(e) => patch("footer", { phone: e.target.value })} /></Field>
        <Field label="Support email"><input className={inputCls} value={site.footer.email || ""} onChange={(e) => patch("footer", { email: e.target.value })} /></Field>
        <Field label="Address line 1"><input className={inputCls} value={site.footer.address_line1 || ""} onChange={(e) => patch("footer", { address_line1: e.target.value })} /></Field>
        <Field label="Address line 2"><input className={inputCls} value={site.footer.address_line2 || ""} onChange={(e) => patch("footer", { address_line2: e.target.value })} /></Field>
        <Field label="City / region"><input className={inputCls} value={site.footer.address_city || ""} onChange={(e) => patch("footer", { address_city: e.target.value })} /></Field>
        <Field label="Instagram URL"><input className={inputCls} value={site.footer.instagram || ""} onChange={(e) => patch("footer", { instagram: e.target.value })} /></Field>
        <Field label="LinkedIn URL"><input className={inputCls} value={site.footer.linkedin || ""} onChange={(e) => patch("footer", { linkedin: e.target.value })} /></Field>
        <Field label="X / Twitter URL"><input className={inputCls} value={site.footer.twitter || ""} onChange={(e) => patch("footer", { twitter: e.target.value })} /></Field>
        <Field label="Facebook URL"><input className={inputCls} value={site.footer.facebook || ""} onChange={(e) => patch("footer", { facebook: e.target.value })} /></Field>
        <Field label="Copyright line"><input className={inputCls} value={site.footer.copyright || ""} onChange={(e) => patch("footer", { copyright: e.target.value })} /></Field>
        <Field label="Footer pillars line"><input className={inputCls} value={site.footer.pillars || ""} onChange={(e) => patch("footer", { pillars: e.target.value })} /></Field>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Booking declaration &amp; terms</h2>
        <p className="text-xs text-[#6b7280]">These start with the same text customers already see at checkout. Edit and save to change the live booking page. HTML is allowed. Links with href #terms, #privacy or #medical open a popup; after it closes the customer stays on the same step.</p>
        <HtmlEditor key={`decl-${cmsReady}`} label="Scrollable declaration box" value={site.legal?.declaration_html || ""} onChange={(declaration_html) => patch("legal", { declaration_html })} />
        <HtmlEditor key={`terms-${cmsReady}`} label="Terms & Conditions popup" value={site.legal?.terms_html || ""} onChange={(terms_html) => patch("legal", { terms_html })} />
        <HtmlEditor key={`privacy-${cmsReady}`} label="Privacy Notice popup" value={site.legal?.privacy_html || ""} onChange={(privacy_html) => patch("legal", { privacy_html })} />
        <HtmlEditor key={`medical-${cmsReady}`} label="Medical declaration popup" value={site.legal?.medical_html || ""} onChange={(medical_html) => patch("legal", { medical_html })} />
        <HtmlEditor key={`check-${cmsReady}`} label="Accept-checkbox text (use #terms / #privacy / #medical links)" value={site.legal?.checkbox_html || ""} onChange={(checkbox_html) => patch("legal", { checkbox_html })} />
      </div>

      <button type="button" disabled={saving} onClick={save}
        className="bg-[#f37721] text-white px-5 py-2.5 rounded-md text-sm font-semibold" data-testid="save-website-cms">
        {saving ? "Saving…" : "Save website content"}
      </button>
    </div>
  );
}

function formatAlertPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  const ten = d.length >= 12 && d.startsWith("91") ? d.slice(-10) : d.slice(-10);
  return ten ? `+91 ${ten}` : String(raw || "");
}

export function AlertRecipients({ onMsg }) {
  const { getAuthHeaders } = useAuth();
  const [text, setText] = useState("");
  const [phonesText, setPhonesText] = useState("");
  const [effective, setEffective] = useState([]);
  const [effectivePhones, setEffectivePhones] = useState([]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${config.API}/admin/config/alert-recipients`, { headers: getAuthHeaders() });
      setText((data.emails || []).join("\n"));
      setPhonesText((data.phones || []).map(formatAlertPhone).join("\n"));
      setEffective(data.effective || []);
      setEffectivePhones(data.effective_phones || []);
    } catch (e) {
      onMsg?.(e.response?.data?.detail || "Could not load alert recipients");
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="alert-recipients">
      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">New-booking emails</h2>
        <p className="text-xs text-[#6b7280]">One email per line. These people are emailed when a customer places a booking. Environment ADMIN_ALERT_TO is always included as well.</p>
        <textarea className={inputCls} rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="ops@bookmyjab.com" />
        {effective.length > 0 && (
          <p className="text-xs text-[#6b7280]">Currently emailing: {effective.join(", ")}</p>
        )}
      </div>
      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">New-booking SMS &amp; WhatsApp</h2>
        <p className="text-xs text-[#6b7280]">One Indian mobile number per line. The same people get SMS and WhatsApp when a booking is placed. Environment ADMIN_ALERT_PHONE / ADMIN_WHATSAPP is always included as well. MSG91 must be configured on the server.</p>
        <textarea className={inputCls} rows={6} value={phonesText} onChange={(e) => setPhonesText(e.target.value)} placeholder={"+91 98735 54343\n9873554343"} />
        {effectivePhones.length > 0 && (
          <p className="text-xs text-[#6b7280]">Currently messaging: {effectivePhones.map(formatAlertPhone).join(", ")}</p>
        )}
      </div>
      <button type="button" className="bg-[#673c96] text-white px-4 py-2 rounded-md text-sm font-medium"
        onClick={async () => {
          const emails = text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
          const phones = phonesText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
          try {
            const { data } = await axios.put(`${config.API}/admin/config/alert-recipients`, { emails, phones }, { headers: getAuthHeaders() });
            setEffective(data.effective || []);
            setEffectivePhones(data.effective_phones || []);
            setPhonesText((data.phones || []).map(formatAlertPhone).join("\n"));
            onMsg?.("Alert emails and SMS/WhatsApp contacts saved");
          } catch (e) {
            onMsg?.(e.response?.data?.detail || "Could not save recipients");
          }
        }}>
        Save recipients
      </button>
    </div>
  );
}

export function NurseAvailabilityAdmin({ onMsg }) {
  const { getAuthHeaders } = useAuth();
  const [staff, setStaff] = useState([]);
  const [nurseId, setNurseId] = useState("");
  const [avail, setAvail] = useState(null);
  const [date, setDate] = useState(localIsoDate());
  const [hours, setHours] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${config.API}/admin/staff`, { params: { role: "nurse" }, headers: getAuthHeaders() }),
      axios.get(`${config.API}/admin/staff`, { params: { role: "doctor" }, headers: getAuthHeaders() }).catch(() => ({ data: { items: [] } })),
    ]).then(([n, d]) => {
      const nurses = (n.data.items || []).map((x) => ({ ...x, _kind: "Nurse" }));
      const docs = (d.data.items || []).map((x) => ({ ...x, _kind: "Doctor" }));
      setStaff([...nurses, ...docs]);
    }).catch(() => {});
  }, [getAuthHeaders]);

  const reloadAvail = (id, day) => {
    if (!id) return;
    setLoading(true);
    axios.get(`${config.API}/admin/staff/${id}/availability`, {
      params: { from_date: day, to_date: addDaysIso(day, 14) },
      headers: getAuthHeaders(),
    }).then(({ data }) => {
      setAvail(data);
      const found = (data.days || []).find((d) => d.date === day);
      setHours(found?.hours || []);
    }).catch((e) => onMsg?.(e.response?.data?.detail || "Could not load availability"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadAvail(nurseId, date);
  }, [nurseId, date, getAuthHeaders]);

  const toggleHour = (hour) => {
    setHours((prev) => (prev.includes(hour) ? prev.filter((h) => h !== hour) : [...prev, hour].sort()));
  };

  const persist = async (apply) => {
    if (!nurseId) return;
    setSaving(true);
    try {
      await axios.put(`${config.API}/admin/staff/${nurseId}/availability`, { date, hours, ...(apply ? { apply } : {}) }, { headers: getAuthHeaders() });
      onMsg?.(apply ? `Hours applied to this ${apply}` : "Hours saved.");
      reloadAvail(nurseId, date);
    } catch (e) {
      onMsg?.(e.response?.data?.detail || "Could not save hours");
    } finally {
      setSaving(false);
    }
  };

  const dayCounts = Object.fromEntries((avail?.days || []).map((d) => [d.date, (d.hours || []).length]));

  return (
    <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3" data-testid="nurse-slot-admin">
      <h2 className="font-semibold text-[#311b52]">Nurse and doctor hours</h2>
      <p className="text-xs text-[#6b7280]">Pick a person and tap time ranges for each weekday. Save, or apply the same hours across the week or month.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <select className={inputCls} value={nurseId} onChange={(e) => setNurseId(e.target.value)}>
          <option value="">Select nurse or doctor…</option>
          {staff.map((n) => <option key={n.id} value={n.id}>{n._kind} · {n.full_name || n.phone}</option>)}
        </select>
        <DmyDateInput className={inputCls} value={date} onChange={setDate} />
      </div>
      {loading && <p className="text-sm text-[#673c96]">Loading hours…</p>}
      {avail && (
        <WeeklyAvailabilityPanel
          selected={date}
          onSelectDay={setDate}
          slotHours={avail.slot_hours || []}
          hours={hours}
          dayCounts={dayCounts}
          onToggleHour={toggleHour}
          onSave={() => persist()}
          onApplyWeek={() => persist("week")}
          onApplyMonth={() => persist("month")}
          saving={saving}
          bookable={Boolean(nurseId)}
        />
      )}
    </div>
  );
}
